import { t } from '../../strings';
import { el, ensureStyle } from '../dom';

// A menu of options, anchored under whatever opened it.
//
// The board used native `<select>`s first, and they were the wrong control for
// two reasons that both matter here. A select renders its closed state and its
// options as plain text: no glyph beside a value, and no way to show a value
// already spent as anything but a word with "— tried" bolted on. And its options
// cannot wrap, so a thirty-bird list in a 70px column is thirty truncations.
//
// This is `species-picker.ts`'s popover with the species taken out of it — same
// contract, because it is the one this app has already settled: fixed to the
// trigger's viewport rect and clamped inside the screen, scrim behind, Escape and
// click-outside to close, arrows to walk and Enter to take. What it adds is a
// leading node per row (a glyph, a thumbnail) and rows that can be present and
// unpickable, which is what an elimination looks like.

ensureStyle(
  'pick-menu-css',
  `
  .pmenu-scrim { position:fixed; inset:0; z-index:60; background:rgba(0,0,0,.18); }
  .pmenu {
    position:fixed; z-index:61; display:flex; flex-direction:column;
    background:var(--card); color:var(--ink);
    border:1.5px solid var(--hairline-strong); border-radius:var(--radius-lg);
    box-shadow:var(--shadow); overflow:hidden;
  }
  .pmenu .pm-head { padding:8px 12px 6px; border-bottom:1.5px solid var(--hairline); }
  .pmenu .pm-list { overflow-y:auto; overscroll-behavior:contain; padding:4px 0 6px; }
  .pmenu .pm-row { display:flex; align-items:center; gap:10px; width:100%;
    padding:9px 13px; background:none; border:none; text-align:left;
    font-family:var(--font-display); font-size:15px; color:var(--ink); }
  .pmenu .pm-row:hover:not(:disabled), .pmenu .pm-row.on { background:var(--sunk); }
  .pmenu .pm-row .lead { flex:none; display:flex; align-items:center;
    justify-content:center; width:24px; color:var(--ink-soft); }
  .pmenu .pm-row .pm-thumb { width:26px; height:26px; border-radius:7px;
    border:1.5px solid var(--hairline); }
  .pmenu .pm-row .txt { flex:1; min-width:0; }
  /* Spent, and kept on the list. The elimination was paid for with a turn, and a
     list that quietly shortened would take the information away with the row. */
  .pmenu .pm-row:disabled { opacity:.42; }
  .pmenu .pm-row:disabled .txt { text-decoration:line-through; }
  .pmenu .pm-row .gone { flex:none; font-size:10px; letter-spacing:.08em;
    text-transform:uppercase; color:var(--ink-soft); }
  .pmenu .pm-row.picked { color:var(--accent); }
  /* Pinned under the list rather than scrolled with it, so it is there however
     long the list is. */
  .pmenu .pm-foot { flex:none; padding:8px 13px 9px; border-top:1.5px solid var(--hairline);
    font-size:12.5px; }
  .pmenu .pm-foot a { color:var(--ink-soft); }
`,
);

/** How tall the menu may get, as a fraction of the viewport. */
const MAX_HEIGHT = 0.6;

/** Narrower than this and the rows wrap; the menu widens past its trigger instead. */
const MIN_WIDTH = 210;

/** Breathing room between the menu and the edge of the screen. */
const EDGE = 10;

export interface PickOption {
  value: string;
  label: string;
  /** A glyph or a thumbnail, drawn ahead of the label. */
  lead?: HTMLElement | null;
  /** Present but unpickable, with a word for why. */
  spent?: boolean;
}

export interface PickMenuOptions {
  title?: string;
  options: PickOption[];
  /** The value currently chosen, marked and scrolled to. */
  current?: string;
  onPick: (value: string) => void;
  /** A line under the list — Birdle's link to what the values mean. */
  foot?: HTMLElement | null;
}

/**
 * Open the menu under `near`. Resolves nothing — the pick arrives through
 * `onPick`, and dismissal is silent, which is what a menu closing means.
 */
export function openPickMenu(near: HTMLElement, opts: PickMenuOptions): void {
  const list = el('div.pm-list', { role: 'listbox' });
  const panel = el(
    'div.pmenu',
    { role: 'dialog', 'aria-label': opts.title ?? t('common.choose') },
    opts.title ? el('div.pm-head.label', {}, opts.title) : null,
    list,
    opts.foot ? el('div.pm-foot', {}, opts.foot) : null,
  );
  const scrim = el('div.pmenu-scrim', {});

  const rows: HTMLButtonElement[] = [];
  for (const option of opts.options) {
    const row = el(
      `button.pm-row${option.value === opts.current ? '.picked' : ''}`,
      { type: 'button', role: 'option' },
      el('span.lead', {}, option.lead ?? null),
      el('span.txt', {}, option.label),
      option.spent ? el('span.gone', {}, 'tried') : null,
    ) as HTMLButtonElement;
    if (option.spent) {
      row.disabled = true;
    } else {
      row.addEventListener('click', () => {
        close();
        opts.onPick(option.value);
      });
    }
    list.append(row);
    rows.push(row);
  }

  /** The row the keyboard is on, as an index into the pickable ones. */
  let cursor = -1;
  const live = (): HTMLButtonElement[] => rows.filter((r) => !r.disabled);

  const paintCursor = (): void => {
    const shown = live();
    cursor = Math.max(-1, Math.min(cursor, shown.length - 1));
    for (const r of rows) r.classList.remove('on');
    const at = shown[cursor];
    if (at) {
      at.classList.add('on');
      at.scrollIntoView({ block: 'nearest' });
    }
  };

  let closed = false;
  function close(): void {
    if (closed) return;
    closed = true;
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('resize', place);
    window.removeEventListener('hashchange', close);
    scrim.remove();
    panel.remove();
    // Back to whatever opened the menu, so a keyboard user is not dropped at the
    // top of the document.
    near.focus?.();
  }

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    // A menu that is up owns all four arrows whether it uses them or not: the
    // screens it opens over read them too, and one of those moving underneath an
    // open menu is how you answer a question you are no longer looking at.
    if (e.key.startsWith('Arrow')) {
      e.preventDefault();
      e.stopPropagation();
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const shown = live();
      if (!shown.length) return;
      cursor = (cursor + (e.key === 'ArrowDown' ? 1 : -1) + shown.length) % shown.length;
      paintCursor();
      return;
    }
    if (e.key === 'Enter') {
      const shown = live();
      if (cursor < 0 || !shown[cursor]) return;
      e.preventDefault();
      // Stopped as well as prevented: the screen behind has an Enter of its own,
      // and taking an option must not also press the button underneath the menu.
      e.stopPropagation();
      shown[cursor].click();
    }
  }

  /**
   * Put the panel under its trigger, then drag it back inside the screen.
   *
   * Fixed positioning against the trigger's viewport rect, so this has to be
   * redone on a resize — a rotation, or the on-screen keyboard opening.
   */
  function place(): void {
    const box = near.getBoundingClientRect();
    const width = Math.min(Math.max(box.width, MIN_WIDTH), window.innerWidth - EDGE * 2);
    const maxHeight = Math.round(window.innerHeight * MAX_HEIGHT);
    panel.style.width = `${width}px`;

    let left = box.left;
    if (left + width > window.innerWidth - EDGE) left = window.innerWidth - EDGE - width;
    if (left < EDGE) left = EDGE;

    // Below by preference, above when there is more room up there — a cell near
    // the foot of the board would otherwise get a menu two rows tall.
    const below = window.innerHeight - box.bottom - EDGE;
    const above = box.top - EDGE;
    const drop = below >= Math.min(maxHeight, 220) || below >= above;
    panel.style.left = `${Math.round(left)}px`;
    if (drop) {
      panel.style.top = `${Math.round(box.bottom + 6)}px`;
      panel.style.bottom = 'auto';
      panel.style.maxHeight = `${Math.min(maxHeight, below)}px`;
    } else {
      panel.style.bottom = `${Math.round(window.innerHeight - box.top + 6)}px`;
      panel.style.top = 'auto';
      panel.style.maxHeight = `${Math.min(maxHeight, above)}px`;
    }
  }

  scrim.addEventListener('click', close);
  // Capture, so Escape closes the menu rather than reaching the surface behind
  // it, which has an Escape handler of its own.
  window.addEventListener('keydown', onKey, true);
  window.addEventListener('resize', place);
  // Any navigation at all leaves the menu orphaned over a screen it was never
  // opened from.
  window.addEventListener('hashchange', close);

  document.body.append(scrim, panel);
  place();
  // Open on what is already chosen, so a menu reopened to change a mind starts
  // where the mind was.
  const at = opts.current ? rows.findIndex((r) => r.classList.contains('picked')) : -1;
  if (at >= 0) rows[at].scrollIntoView({ block: 'center' });
  panel.focus?.();
}
